// ============================================================================
// Supply Management — Create Purchase Order
// Converts one or more Approved supply requests into a PO. User picks the
// vendor manually (Phase 1 — vendor optimization deferred to Phase 2). On
// submit:
//   1) INSERT purchase_orders (status='Draft', urgency = max of selected reqs)
//   2) INSERT purchase_order_lines for each selected request, linking back to
//      the source request_id and stamping facility/account/item
//   3) UPDATE supply_requests.status = 'Ordered', po_id = <new id>
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { supabase } from '../../shared/supabase';
import {
  ErrorState,
  PageHeader,
  UrgencyBadge,
} from '../../shared/components';
import type {
  PreferredVendor,
  SupplyRequest,
  SupplyUrgency,
} from '../../shared/types';
import { SupplySubNav } from './supply-sub-nav';
import { errorMessage } from '../../shared/errors';

interface ApprovedRow extends SupplyRequest {
  facility_name: string | null;
  supply_name: string | null;
  supply_category: string | null;
  unit_cost_default: number | null;
}

const URGENCY_RANK: Record<SupplyUrgency, number> = {
  Emergency: 0,
  Soon: 1,
  Planned: 2,
};

export default function NewPOForm() {
  const navigate = useNavigate();
  const { employee } = useAuth();

  const [approved, setApproved] = useState<ApprovedRow[]>([]);
  const [vendors, setVendors] = useState<PreferredVendor[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [vendorId, setVendorId] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('Net 30');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: flat, error: e } = await supabase
        .from('supply_requests')
        .select(
          `request_id, facility_id, account_id, supply_id, item_description,
           quantity, unit_of_measure, urgency, date_needed, requested_by,
           approved_by, approved_at, ship_to_address, reimbursable, notes,
           status, estimated_cost, vendor_selected, created_at, updated_at`,
        )
        .eq('status', 'Approved')
        .is('po_id', null)
        .order('created_at', { ascending: true });
      if (e) throw e;
      const flatRows = (flat ?? []) as unknown as SupplyRequest[];

      const facIds = Array.from(
        new Set(flatRows.map((r) => r.facility_id).filter(Boolean)),
      ) as string[];
      const supIds = Array.from(
        new Set(flatRows.map((r) => r.supply_id).filter(Boolean)),
      ) as string[];

      const [facRes, supRes, vRes] = await Promise.all([
        facIds.length === 0
          ? Promise.resolve({ data: [] as unknown[], error: null })
          : supabase
              .from('facilities')
              .select('facility_id, facility_name')
              .in('facility_id', facIds),
        supIds.length === 0
          ? Promise.resolve({ data: [] as unknown[], error: null })
          : supabase
              .from('supplies')
              .select('supply_id, item_name, supply_category, unit_cost')
              .in('supply_id', supIds),
        supabase
          .from('preferred_vendors')
          .select(
            'vendor_id, vendor_name, primary_category, lead_time_days, same_day_capable, status, unit_price_avg, case_price_avg, delivery_fee',
          )
          .eq('status', 'Active')
          .order('vendor_name'),
      ]);

      const facMap: Record<string, string | null> = {};
      for (const f of (facRes.data ?? []) as Array<{
        facility_id: string;
        facility_name: string | null;
      }>) {
        facMap[f.facility_id] = f.facility_name;
      }
      const supMap: Record<
        string,
        { item_name: string | null; supply_category: string | null; unit_cost: number | null }
      > = {};
      for (const s of (supRes.data ?? []) as Array<{
        supply_id: string;
        item_name: string | null;
        supply_category: string | null;
        unit_cost: number | null;
      }>) {
        supMap[s.supply_id] = {
          item_name: s.item_name,
          supply_category: s.supply_category,
          unit_cost: s.unit_cost,
        };
      }

      setApproved(
        flatRows.map((r) => ({
          ...r,
          facility_name: r.facility_id ? facMap[r.facility_id] ?? null : null,
          supply_name: r.supply_id ? supMap[r.supply_id]?.item_name ?? null : null,
          supply_category: r.supply_id
            ? supMap[r.supply_id]?.supply_category ?? null
            : null,
          unit_cost_default: r.supply_id
            ? supMap[r.supply_id]?.unit_cost ?? null
            : null,
        })),
      );
      setVendors((vRes.data ?? []) as unknown as PreferredVendor[]);
    } catch (e) {
      console.error('[new-po-form] load failed', e);
      setError(errorMessage(e, 'Failed to load.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pickedRows = useMemo(
    () => approved.filter((r) => picked.has(r.request_id)),
    [approved, picked],
  );

  const urgencyTier = useMemo<SupplyUrgency>(() => {
    if (pickedRows.length === 0) return 'Planned';
    let best: SupplyUrgency = 'Planned';
    let bestRank = URGENCY_RANK['Planned'];
    for (const r of pickedRows) {
      const u = (r.urgency as SupplyUrgency) ?? 'Planned';
      const rank = URGENCY_RANK[u] ?? 2;
      if (rank < bestRank) {
        bestRank = rank;
        best = u;
      }
    }
    return best;
  }, [pickedRows]);

  const subtotal = useMemo(() => {
    return pickedRows.reduce((acc, r) => {
      const unit = r.unit_cost_default ?? 0;
      return acc + unit * Number(r.quantity ?? 0);
    }, 0);
  }, [pickedRows]);

  const submit = async () => {
    if (pickedRows.length === 0) {
      alert('Pick at least one approved request.');
      return;
    }
    if (!vendorId) {
      alert('Pick a vendor.');
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const firstFac = pickedRows.find((r) => r.facility_id);
      const vendor = vendors.find((v) => v.vendor_id === vendorId);

      const { data: newPO, error: poErr } = await supabase
        .from('purchase_orders')
        .insert({
          po_date: today,
          requested_by_eid: employee?.employee_id ?? null,
          requested_by_name: employee
            ? [employee.employee_name_first, employee.employee_name_last]
                .filter(Boolean)
                .join(' ')
                .trim() || null
            : null,
          vendor_id: vendorId,
          vendor_name: vendor?.vendor_name ?? null,
          facility_id: firstFac?.facility_id ?? null,
          facility_name: firstFac?.facility_name ?? null,
          account_id: firstFac?.account_id ?? null,
          ship_to_address: firstFac?.ship_to_address ?? null,
          urgency: urgencyTier,
          status: 'Draft',
          payment_method: paymentMethod || null,
          subtotal,
          total: subtotal,
          notes: notes || null,
          submitted_date: today,
        })
        .select('po_id')
        .single();
      if (poErr) throw poErr;
      const poId = (newPO as { po_id: string }).po_id;

      const lineRows = pickedRows.map((r) => ({
        po_id: poId,
        request_id: r.request_id,
        supply_id: r.supply_id,
        item_name: r.supply_name ?? r.item_description,
        qty: Number(r.quantity ?? 0),
        unit: r.unit_of_measure ?? null,
        unit_price: r.unit_cost_default,
        extended_price:
          r.unit_cost_default != null
            ? r.unit_cost_default * Number(r.quantity ?? 0)
            : null,
        facility_id: r.facility_id,
        account_id: r.account_id,
        category: r.supply_category,
        reimbursable: r.reimbursable ? 'Yes' : 'No',
        line_status: 'Ordered',
        ship_to_address: r.ship_to_address,
      }));
      const { error: lineErr } = await supabase
        .from('purchase_order_lines')
        .insert(lineRows);
      if (lineErr) throw lineErr;

      const { error: reqErr } = await supabase
        .from('supply_requests')
        .update({ status: 'Ordered', po_id: poId })
        .in(
          'request_id',
          pickedRows.map((r) => r.request_id),
        );
      if (reqErr) throw reqErr;

      navigate(`/supply/orders/${encodeURIComponent(poId)}`);
    } catch (e) {
      console.error('[new-po-form] submit failed', e);
      alert(`Create PO failed: ${errorMessage(e, String(e))}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <SupplySubNav />

      <Link
        to="/supply/orders"
        className="mb-3 inline-flex items-center text-sm text-tcs-text-secondary hover:text-tcs-text"
      >
        <ChevronLeft className="mr-1 h-4 w-4" />
        Back to orders
      </Link>

      <PageHeader
        title="Create purchase order"
        meta={`${picked.size} of ${approved.length} approved selected`}
      />

      {error && (
        <div className="mt-4">
          <ErrorState body={error} onRetry={load} />
        </div>
      )}

      {!loading && approved.length === 0 && (
        <div className="mt-4 rounded-md border border-tcs-border bg-tcs-surface p-6 text-center text-sm text-tcs-text-secondary">
          No approved requests pending order. Approve one in the queue first.
        </div>
      )}

      {!loading && approved.length > 0 && (
        <>
          <section className="mt-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              Approved requests
            </h2>
            <ul className="space-y-2">
              {approved.map((r) => {
                const isOn = picked.has(r.request_id);
                return (
                  <li
                    key={r.request_id}
                    onClick={() => toggle(r.request_id)}
                    className={[
                      'cursor-pointer rounded-md border p-3 transition-colors',
                      isOn
                        ? 'border-tcs-text bg-st-info-bg'
                        : 'border-tcs-border bg-tcs-surface hover:bg-tcs-bg',
                    ].join(' ')}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isOn}
                        readOnly
                        className="mt-1 h-4 w-4"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <UrgencyBadge tier={r.urgency as SupplyUrgency} />
                          <span className="font-mono text-xs text-tcs-text-secondary">
                            {r.request_id}
                          </span>
                        </div>
                        <div className="mt-1 text-sm font-medium text-tcs-text">
                          {r.quantity} × {r.unit_of_measure ?? ''}{' '}
                          {r.supply_name ?? r.item_description}
                        </div>
                        <div className="mt-1 text-xs text-tcs-text-secondary">
                          {r.facility_name ?? r.facility_id ?? 'Unknown'}{' '}
                          {r.unit_cost_default != null
                            ? `· $${(r.unit_cost_default * Number(r.quantity ?? 0)).toFixed(2)}`
                            : ''}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="mt-6 rounded-md border border-tcs-border bg-tcs-surface p-4">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">
              Order details
            </h2>
            <div className="space-y-3">
              <label className="block">
                <span className="block text-sm font-medium text-tcs-text">
                  Vendor
                </span>
                <select
                  value={vendorId}
                  onChange={(e) => setVendorId(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base"
                >
                  <option value="">— pick a vendor —</option>
                  {vendors.map((v) => (
                    <option key={v.vendor_id} value={v.vendor_id}>
                      {v.vendor_name ?? v.vendor_id}
                      {v.primary_category ? ` — ${v.primary_category}` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-tcs-text">
                  Payment terms
                </span>
                <input
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base"
                  placeholder="Net 30, ACH, Card on file…"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-tcs-text">
                  Notes
                </span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base"
                  placeholder="Optional notes for the vendor or internal record."
                />
              </label>

              <div className="flex items-center justify-between border-t border-tcs-border pt-3 text-sm">
                <span className="text-tcs-text-secondary">
                  Urgency tier: <strong>{urgencyTier}</strong>
                </span>
                <span className="font-semibold text-tcs-text">
                  Subtotal: ${subtotal.toFixed(2)}
                </span>
              </div>

              <button
                disabled={submitting || picked.size === 0 || !vendorId}
                onClick={submit}
                className="block w-full rounded-md bg-tcs-inverse px-4 py-2.5 text-base font-medium text-tcs-inverse-ink hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Creating…' : 'Create PO'}
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
